import { useEffect, useRef, useState, type RefObject } from 'react';

const GRID_GAP_PX = 8;
const HORIZONTAL_PADDING_PX = 8;
const MIN_THUMB_WIDTH_PX = 80;
const MAX_THUMB_WIDTH_PX = 128;
const DEFAULT_COLUMNS_DESKTOP = 2;
const DEFAULT_COLUMNS_MOBILE = 1;

/**
 * Locks thumbnail cell width from the first container measurement (initial 2-col layout)
 * and uses CSS auto-fill so extra horizontal space fits more thumbnails per row.
 */
export function useThumbnailGridLayout(
  containerRef: RefObject<HTMLElement | null>,
  enabled: boolean,
  isMobileScreen: boolean
) {
  const [thumbnailWidth, setThumbnailWidth] = useState<number | null>(null);
  const lockedWidthRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) {
      lockedWidthRef.current = null;
      setThumbnailWidth(null);
      return;
    }

    const element = containerRef.current;
    if (!element) {
      return;
    }

    const lockWidthFromContainer = (containerWidth: number) => {
      if (!containerWidth || lockedWidthRef.current !== null) {
        return;
      }

      const columns = isMobileScreen ? DEFAULT_COLUMNS_MOBILE : DEFAULT_COLUMNS_DESKTOP;
      const availableWidth = containerWidth - HORIZONTAL_PADDING_PX;
      const rawWidth = Math.floor(
        (availableWidth - (columns - 1) * GRID_GAP_PX) / columns
      );
      const lockedWidth = Math.min(
        MAX_THUMB_WIDTH_PX,
        Math.max(MIN_THUMB_WIDTH_PX, rawWidth)
      );

      lockedWidthRef.current = lockedWidth;
      setThumbnailWidth(lockedWidth);
    };

    lockWidthFromContainer(element.clientWidth);

    const resizeObserver = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width;
      if (width) {
        lockWidthFromContainer(width);
      }
    });

    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, [containerRef, enabled, isMobileScreen]);

  const gridStyle =
    enabled && thumbnailWidth
      ? {
          gridTemplateColumns: `repeat(auto-fill, ${thumbnailWidth}px)`,
          justifyContent: 'start' as const,
        }
      : undefined;

  return { thumbnailWidth, gridStyle };
}
